import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Listbox } from "@headlessui/react";
import Button from "@surgia/ui/components/button";
import GenericModal from "@surgia/ui/components/genericModal";
import { lookupQueries } from "@surgia/api/src/queryStore/shared/lookup";
import { validatePriceRange, isValidNumericInput } from "./validation";
import {
  filterInitialData,
  ProductFilters,
  ProductFiltersModalProps,
} from "./constants";

const ProductFiltersModal: React.FC<ProductFiltersModalProps> = ({
  open,
  onClose,
  onApply,
  filters,
  setFilters,
  tenantId,
  showPriceRange = true,
  showCategories = true,
  filterWithTenantCategoryParam = false,
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] =
    React.useState<ProductFilters>(filters);
  const isPriceRangeValid = validatePriceRange(
    localFilters.price_from || "",
    localFilters.price_to || ""
  );

  // queries
  const { data: categories } = useQuery({
    ...lookupQueries.queries.categories({
      storefront_id: 2,
      tenant_id: +tenantId,
    }),
    enabled: showCategories && !filterWithTenantCategoryParam,
  });

  const { data: tenantFilters } = useQuery(
    lookupQueries.queries.tenantFilters({
      tenant_id: +tenantId,
      component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
    })
  );

  // handlers
  const handleApply = () => {
    if (
      (localFilters.price_from && !localFilters.price_to) ||
      (!localFilters.price_from && localFilters.price_to)
    ) {
      toast.error(t("admin.productAssignment.filters.priceFilterError"));
      return;
    }

    if (
      localFilters.price_from ||
      localFilters.price_to ||
      (filterWithTenantCategoryParam && localFilters.category_name) ||
      (!filterWithTenantCategoryParam && localFilters.category) ||
      localFilters.brandName
    ) {
      setFilters(localFilters);
    }
    onApply(localFilters);
  };

  const handleClear = () => {
    setLocalFilters(filterInitialData);
    setFilters(filterInitialData);
  };

  const handlePriceToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (isValidNumericInput(value)) {
      setLocalFilters({ ...localFilters, price_to: value });
    }
  };

  const handlePriceFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (isValidNumericInput(value)) {
      setLocalFilters({ ...localFilters, price_from: value });
    }
  };

  // Keep localFilters in sync with parent filters when modal opens
  React.useEffect(() => {
    if (open) setLocalFilters(filters);
  }, [open]);

  // Find selected category and brand objects
  const selectedCategory = filterWithTenantCategoryParam
    ? tenantFilters?.data?.["categories"]?.find(
        (category: string) => category === localFilters.category_name
      ) || null
    : categories?.data?.find(
        (category: any) => category.id.toString() === localFilters.category
      ) || null;
  const selectedBrand =
    tenantFilters?.data?.["brands"]?.find(
      (brand: string) => brand === localFilters.brandName
    ) || null;
  return (
    <GenericModal
      title={t("filters")}
      open={open}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={handleClear} className="font-medium">
            {t("admin.clearFilters")}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleApply}
            disabled={!isPriceRangeValid}
          >
            {t("filter")}
          </Button>
        </div>
      }
      additionalStyles="!overflow-visible"
    >
      <div className="space-y-4">
        {/* <PriceRangeFilter /> */}
        {showPriceRange && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("admin.productAssignment.filters.priceRange")}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={localFilters.price_from || ""}
                onChange={handlePriceFromChange}
                placeholder="0"
                className="w-full px-3 py-2 border !text-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                inputMode="numeric"
              />
              <span className="text-gray-500">-</span>
              <input
                type="text"
                value={localFilters.price_to || ""}
                onChange={handlePriceToChange}
                placeholder="100"
                className="w-full px-3 py-2 border !text-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                inputMode="numeric"
              />
            </div>
            {/* Price range validation error */}
            <p className="text-red-500 text-sm mt-[2px]">
              {!isPriceRangeValid &&
                "Minimum price should be less than maximum price"}
            </p>
          </div>
        )}

        {/* Category Filter - Listbox */}
        {showCategories && (
          <div>
            <label className="block text-[14px] mb-1">
              {t("admin.productAssignment.filters.category")}
            </label>
            <Listbox
              value={selectedCategory}
              onChange={(selectedCategory) => {
                setLocalFilters({
                  ...localFilters,
                  ...(filterWithTenantCategoryParam
                    ? { category_name: selectedCategory || "" }
                    : { category: selectedCategory?.id?.toString() || "" }),
                });
              }}
            >
              <div className="relative">
                <Listbox.Button className="w-full border rounded-lg px-4 py-2 text-left bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-400">
                  <span className="block truncate">
                    {selectedCategory
                      ? filterWithTenantCategoryParam
                        ? selectedCategory
                        : selectedCategory.name
                      : t("admin.productAssignment.filters.selectCategory")}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </Listbox.Button>

                <Listbox.Options className="absolute z-[1000] mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                  <Listbox.Option
                    value={null}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                        >
                          {t("admin.productAssignment.filters.selectCategory")}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                            <svg
                              className="w-5 h-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                  {(filterWithTenantCategoryParam
                    ? tenantFilters?.data?.["categories"]
                    : categories?.data
                  )?.map((category: any) => (
                    <Listbox.Option
                      key={
                        filterWithTenantCategoryParam ? category : category.id
                      }
                      value={
                        filterWithTenantCategoryParam ? category : category
                      }
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                          >
                            {filterWithTenantCategoryParam
                              ? category
                              : category.name}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <svg
                                className="w-5 h-5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
          </div>
        )}

        {/* Brand Filter - Listbox */}
        <div>
          <label className="block text-[14px] mb-1">
            {t("admin.productAssignment.filters.brand")}
          </label>
          <Listbox
            value={selectedBrand}
            onChange={(selectedBrand) => {
              setLocalFilters({
                ...localFilters,
                brandName: selectedBrand || "",
              });
            }}
          >
            <div className="relative">
              <Listbox.Button className="w-full border rounded-lg px-4 py-2 text-left bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-400">
                <span className="block truncate">
                  {selectedBrand ||
                    t("admin.productAssignment.filters.selectBrand")}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </Listbox.Button>

              <Listbox.Options className="absolute z-[1000] mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                <Listbox.Option
                  value={null}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                      >
                        {t("admin.productAssignment.filters.selectBrand")}
                      </span>
                      {selected ? (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                          <svg
                            className="w-5 h-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
                {tenantFilters?.data?.["brands"]?.map(
                  (brand: string, index: number) => (
                    <Listbox.Option
                      key={index}
                      value={brand}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                          >
                            {brand}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <svg
                                className="w-5 h-5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  )
                )}
              </Listbox.Options>
            </div>
          </Listbox>
        </div>
      </div>
    </GenericModal>
  );
};

export default ProductFiltersModal;
